import {customElement, property, state} from "lit/decorators.js";
import {html, LitElement, type TemplateResult} from "lit";
import {classMap} from "lit/directives/class-map.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import Styles from "./calendarEntryTile.lit.scss";
import {ifDefined} from "lit/directives/if-defined.js";
import type {Nullable} from "../../../bootstrap/common/types";
import {type CalendarEntry} from "./calendarEntry";
import {Dictionary} from "../../../page/elements/dictionary";
import {renderDateTime, type RenderedDateTime} from "./renderDateTime";
import {FeedService} from "../feedService";
import {resolve} from "../../../container";
import {LanguagesService} from "../../../common/languages";
import {trackingTeaserInteract, trackingTextlinkInteract} from "../../../tracking/trackingAttributes";
import {ShadowHighlightContextAware} from "../../../common/elements";
import {Resolution} from "../../../common/resolution";
import {BOOTSTRAP} from "../../../common/resolutionConstants";


@customElement("eop-calendar-entry-tile")
export class EopCalendarEntryTile extends ShadowHighlightContextAware(LitElement) {

    public static readonly styles = Styles;

    public constructor(
        private feedService: FeedService = resolve(FeedService),
        private languages: LanguagesService = resolve(LanguagesService),
        private resolution: Resolution = resolve(Resolution)
    ) {
        super();
    }

    @property()
    public entry: CalendarEntry;
    @property({type: Boolean, attribute: "enable-mobile-condensed"})
    public enableMobileCondensed: boolean = false;

    @state()
    private mobileCondensed: boolean = false;

    private dictionary: Dictionary;

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.resolution.onBootstrapBreakpointChange(() => this.updateView());
        this.updateView();
    }

    private updateView(): void {
        this.mobileCondensed = this.enableMobileCondensed && this.resolution.upTo(BOOTSTRAP.SM);
    }

    public render(): TemplateResult {
        if (!this.entry.providesDetailInfo()) {
            return html`
                <div class=${classMap({"tile": true, "mobile-condensed": this.mobileCondensed})}>
                    <div class="non-linked-tile">
                        ${this.renderInnerTile()}
                    </div>
                </div>
            `;
        } else if (this.entry.isInfoLayerLink()) {
            const template = `
                <template data-id="content">
                    <div class="modal-dialog info-layer">
                        ${this.entry.infoLayerContent()}
                    </div>
                </template>`;
            return html`
                <div class=${classMap({"tile": true, "mobile-condensed": this.mobileCondensed})}>
                    <eop-inline-modal-dialog-content-link content="content">
                        <div class="label linked-tile"
                             ${trackingTeaserInteract(this.entry.title())}
                             tabindex="0">
                            ${this.renderInnerTile()}
                        </div>
                        ${unsafeHTML(template)}
                    </eop-inline-modal-dialog-content-link>
                </div>
            `;
        } else if (this.entry.isInternalLink() && this.entry.shouldUsePageOverlay()) {
            return html`
                <div class=${classMap({"tile": true, "mobile-condensed": this.mobileCondensed})}>
                    <eop-page-overlay-link
                            href=${this.entry.link()}
                            ${trackingTeaserInteract(this.entry.title(), this.entry.link())}
                            class="linked-tile"
                    >
                        ${this.renderInnerTile()}
                    </eop-page-overlay-link>
                </div>
            `;
        } else {
            return html`
                <div class=${classMap({"tile": true, "mobile-condensed": this.mobileCondensed})}>
                    <eop-background-link
                            href=${this.entry.link()}
                            ${trackingTeaserInteract(this.entry.title(), this.entry.link())}
                            target=${ifDefined(this.entry.isExternalLink() ? "_blank" : undefined)}
                            class="linked-tile"
                    >
                        ${this.renderInnerTile()}
                    </eop-background-link>
                </div>
            `;
        }
    }

    private renderInnerTile(): TemplateResult {
        const renderedDateTime = renderDateTime(this.entry, this.dictionary);
        return html`
            <div class="tile-image">
                ${this.renderImageElement()}
                <eop-calendar-entry-badge
                        .date=${this.entry.start().raw}
                        ?tile-view=${!this.mobileCondensed}
                        class=${this.mobileCondensed ? "reverse-background" : ""}
                >
                </eop-calendar-entry-badge>
            </div>
            <div class="tile-text ${this.mobileCondensed ? "" : "reverse-background"}">
                <div class="date h5">${renderedDateTime.date}</div>
                <div class="title">${unsafeHTML(this.entry.title())}</div>
                <div class="facts">
                    ${this.renderLocation()}
                    ${this.renderTime(renderedDateTime)}
                    ${this.renderDownloadLink()}
                </div>
                ${this.renderDescription()}
            </div>
        `;
    }

    private renderDescription(): Nullable<TemplateResult> {
        return this.entry.text()
            ? html`
                    <div class="description">${unsafeHTML(this.entry.text())}</div>`
            : null;
    }

    private renderLocation(): Nullable<TemplateResult> {
        return this.entry.location()
            ? html`
                    <div class="location">${unsafeHTML(this.entry.location())}</div>`
            : null;
    }

    private renderTime(renderedDateTime: RenderedDateTime): Nullable<TemplateResult> {
        return renderedDateTime.time
            ? html`
                    <div class="time">${renderedDateTime.time}</div>`
            : null;
    }

    private renderDownloadLink(): Nullable<TemplateResult> {
        if (!this.entry.isIcsDownloadEnabled()) {
            return null;
        }
        const href = this.feedService.downloadCalendarEntryLink(this.entry.id(), this.languages.activeLanguageId());
        const downloadLabel = this.dictionary.translate("DOWNLOAD");
        return html`
            <a class="i-calendar-download"
               target="_blank" href=${href} download
               ${trackingTextlinkInteract(downloadLabel + ": " + this.entry.title(), href)}
            >${downloadLabel}</a>`;
    }


    private renderImageElement(): TemplateResult {
        if (this.entry.imageIsResponsive()) {
            return html`
                <eop-responsive-image
                        image-src=${this.entry.imageUrl()}
                        image-alt=${this.entry.imageAltText()}
                ></eop-responsive-image>`;
        } else {
            return html`
                <eop-plain-image
                        src=${this.entry.imageUrl()}
                        alt=${this.entry.imageAltText()}
                        class="eop-image"
                ></eop-plain-image>
            `;
        }
    }
}